import "./COREMenu.less";
import React, { useContext } from "react";
import { Menu, SubMenuProps } from "antd";
import { TestID } from "../../shared/testids/testids";
import { MenuModeContext } from "./COREMenu";
import { CORETypographyInput } from "../Typography/CORETypographyInput";
import { CaretDownOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { COREDivider } from "../Layout/COREDivider";

type CORESubMenuProps = {
  testID: TestID;
  className?: string;
  title?: React.ReactNode;
  popupClassName?: string;
  divider?: boolean;
  /**
   * Forces the title to render with the selected appearance regardless of the
   * parent menu's `selectedKeys`, mirroring `COREMenuItem`'s `selected` prop —
   * used when the current view is reached via `onTitleClick` rather than a
   * nested `COREMenuItem`, so nothing in `selectedKeys` would otherwise mark
   * this submenu's title as active.
   */
  selected?: boolean;
};

export const PopupOffsetContext = React.createContext<
  [number, number] | undefined
>(undefined);

const TitleWithCaretDownIcon: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  return (
    <>
      {children}
      <CaretDownOutlined className={"horizontal-expand-icon"} />
    </>
  );
};

export const CORESubMenu: React.FC<CORESubMenuProps & SubMenuProps> = ({
  testID,
  className,
  popupClassName,
  title,
  divider,
  selected = false,
  ...props
}) => {
  let popupOffset: [number, number] = [0, 0];
  const popupClasses = [popupClassName];
  const memuMode = useContext(MenuModeContext);
  const popupOffsetContext = useContext(PopupOffsetContext);

  if (popupOffsetContext) {
    popupOffset = popupOffsetContext;
  } else if (!popupOffsetContext && memuMode === "horizontal") {
    popupOffset = [-20, 2];
  } else {
    popupOffset = [5, 0];
  }

  let titleMenu: React.ReactNode = (
    <CORETypographyInput type={"default"}>{title}</CORETypographyInput>
  );
  if (memuMode === "horizontal") {
    titleMenu = <TitleWithCaretDownIcon>{titleMenu}</TitleWithCaretDownIcon>;
    popupClasses.push("popup-horizontal");
  } else {
    popupClasses.push("popup-vertical");
  }

  return (
    <PopupOffsetContext.Provider value={[3, -3]}>
      <Menu.SubMenu
        {...props}
        title={titleMenu}
        data-testid={testID}
        className={classNames(className, "core-menu-submenu", {
          "ant-menu-submenu-selected": selected,
        })}
        popupClassName={classNames(popupClasses, "core-menu-popup-container")}
        popupOffset={popupOffset}
      />
      {divider && <COREDivider space={"none"} />}
    </PopupOffsetContext.Provider>
  );
};
